import { Scripts as AdManagerScripts } from '@client/adManager/Scripts.js';
import { Scripts as AdnamiScripts } from '@client/core/utils/Adnami/Scripts.js';
import { Scripts as BrandMetricsScripts } from '@client/core/utils/BrandMetrics/Scripts.js';
import { Scripts as CogwheelScripts } from '@client/core/utils/Cogwheel/Scripts.js';
import type { Script } from '@client/core/utils/Script.js';
import {
  debug as _debug,
  type InitialState,
  ConsentStatus,
  nameof,
  Rgb
} from '@schibsted-nmp/advertising-shared';

export namespace Scripts {
  export const debug = _debug.withLabel(
    nameof({ Scripts }),
    Rgb.brown,
    Rgb.peach
  );

  export function* get(
    initialState: InitialState,
    consent: ConsentStatus
  ): Generator<Script, void, void> {
    const {
      config: {
        brand,
        adServer: { gam }
      }
    } = initialState;

    if (consent === ConsentStatus.Accepted) {
      yield* BrandMetricsScripts.get(brand);
    }

    yield* CogwheelScripts.get();

    if (gam) {
      yield* AdnamiScripts.get(brand);
    }

    yield* AdManagerScripts.get(initialState, false);
  }
  export function* load(
    initialState: InitialState,
    consent: ConsentStatus
  ): Generator<Promise<Event | null>, void, void> {
    for (const script of get(initialState, consent)) {
      yield injectIntoDom(script);
    }
  }

  export async function injectIntoDom({
    name,
    src,
    defer,
    crossOrigin
  }: Script) {
    const debug = Scripts.debug.withSublabel('Scripts', name);
    const ssrScriptElement = document.getElementById(`advt-script-${name}`);

    if (ssrScriptElement) {
      debug
        .message('Injected server-side', ssrScriptElement)
        .success((log) => log());

      return null;
    }

    const { promise, resolve, reject } = Promise.withResolvers<Event>();
    const script = document.createElement('script');
    const isNonCritical = name === 'brand-metrics';

    Object.assign(script, {
      src,
      type: 'text/javascript',
      async: true,
      ...(defer && { defer }),
      ...(crossOrigin && { crossOrigin }),

      onload(event) {
        debug
          .message('Client-side loading completed', event)
          .success((log) => log());

        resolve(event);
      },

      onerror(error) {
        if (isNonCritical) {
          debug
            .message(
              'Client-side loading failed but script is non-critical - continuing without it',
              error
            )
            .warn();

          resolve(error as Event);
          return;
        }

        debug.message('Client-side loading failed!', error).error();
        reject(error);
      }
    } satisfies Partial<typeof script>);

    document.head.appendChild(script);

    return await promise;
  }
}
